import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { trustCenterApi, assessmentsApi } from '../../services/api';
import { getCountsFromAssessments } from '../../utils/assessmentStatus';
import DataTable from '../../components/DataTable';
import { useCrosswalkStatusMap } from '../../hooks/useCrosswalkStatusMap';
import CrosswalkStatusBadge from '../../components/CrosswalkStatusBadge';

const statusStyles = {
  'Pending': 'bg-[#F47F2E]/10 text-[#F47F2E] border border-[#F47F2E]/30',
  'Completed': 'bg-[#0077b5]/10 text-[#0077b5] border border-[#0077b5]/30',
  'On Hold': 'bg-[#919191]/10 text-[#919191] border border-[#919191]/30',
  'Reject': 'bg-[#53545a]/10 text-[#53545a] border border-[#53545a]/30',
};

const ReviewerAssessments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countInfoAssessments, setCountInfoAssessments] = useState([]);

  const fetchAssessments = useCallback(async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await trustCenterApi.getSharedAssessmentsByEmail(
        user.email,
        1,
        1000
      );
      setAssessments(response.data || []);
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
      setError('Failed to load assessments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  useEffect(() => {
    if (!user?.id) return;
    assessmentsApi.CountInfo(user.id)
      .then(data => setCountInfoAssessments(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load count info:', err));
  }, [user?.id]);


  // Use the same logic as ReviewerDashboard for card counts
  const filteredAssessments = useMemo(
    () => countInfoAssessments.filter(a => Array.isArray(a.questionnaires) && a.questionnaires.length > 0),
    [countInfoAssessments]
  );
  const { completed, processing, pending } = getCountsFromAssessments(filteredAssessments);

  const assessmentIds = useMemo(() => assessments.map(a => a.assessmentId).filter(Boolean), [assessments]);
  const crosswalkStatusMap = useCrosswalkStatusMap(assessmentIds);

  const columns = useMemo(() => [
    {
      key: 'vendorName',
      header: 'Business Entity',
      sortable: true,
      filterable: true,
      accessor: (row) => row.vendorName || '',
      render: (row) => <span className="text-sm font-medium text-[#53545a]">{row.vendorName || '-'}</span>,
    },
    {
      key: 'description',
      header: 'Assessment',
      sortable: true,
      filterable: true,
      accessor: (row) => row.description || '',
      render: (row) => {
        const desc = row.description || '-';
        return <span className="text-sm text-[#919191]">{desc.length > 28 ? desc.slice(0, 28) + '...' : desc}</span>;
      },
    },
    {
      key: 'customerOwner',
      header: 'Customer Owner',
      sortable: true,
      filterable: true,
      accessor: (row) => row.customerOwner || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.customerOwner || '-'}</span>,
    },
    // {
    //   key: 'status',
    //   header: 'Status',
    //   sortable: true,
    //   filterable: true,
    //   accessor: (row) => row.status || '',
    //   render: (row) => (
    //     <span className={`px-4 py-1.5 rounded-full text-xs font-semibold ${statusStyles[row.status] || 'bg-[#919191]/10 text-[#919191]'}`}>
    //       {row.status || '-'}
    //     </span>
    //   ),
    // },
    {
      key: 'accountName',
      header: 'Account Name',
      sortable: true,
      filterable: true,
      accessor: (row) => row.accountName || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.accountName || '-'}</span>,
    },
    {
      key: 'crosswalkStatus',
      header: 'Status',
      sortable: false,
      render: (row) => {
        const info = crosswalkStatusMap[row.assessmentId];
        return (
          <CrosswalkStatusBadge
            status={info?.status || 'not_started'}
            completedCount={info?.completedCount}
            runningCount={info?.runningCount}
            failedCount={info?.failedCount}
            total={info?.questionnaireCount}
          />
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <button
          onClick={() => navigate(`/reviewer/questionnaires/${row.assessmentId}`)}
          className="group relative inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#F47F2E] to-[#F47F2E]/85 text-[#ffffff] text-sm font-bold shadow-md hover:shadow-lg hover:scale-[1.04] active:scale-[0.97] transition-all duration-200 overflow-hidden"
        >
          <span className="absolute inset-0 bg-[#ffffff]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></span>
          <FiEye className="text-base relative z-10" />
          <span className="relative z-10">Review</span>
          <FiArrowRight className="text-sm relative z-10 group-hover:translate-x-0.5 transition-transform duration-200" />
        </button>
      )
    }
  ], [navigate, crosswalkStatusMap]);

  return (
    <div className="space-y-6">
      {/* Title */}

      {/* If user has email but data is loading, show page-level skeleton and skip rendering real UI */}
      {user?.email && loading && (
        <div className="space-y-6">
          <div>
            <div className='flex justify-between'>
              <div className="w-64 h-7 bg-[#e6eef5] rounded animate-pulse mb-2" />
              <div className="w-64 h-7 bg-[#e6eef5] rounded animate-pulse mb-2" />
            </div>
            <div className="w-96 h-4 bg-[#e6eef5] rounded animate-pulse" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => (
              <div key={i} className=" p-4 cardStyle">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#e6eef5] animate-pulse flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="w-20 h-3 bg-[#e6eef5] rounded animate-pulse mb-2" />
                    <div className="w-12 h-6 bg-[#e6eef5] rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>


        </div>
      )}

      {/* No Email State */}
      {!user?.email && !loading && (
        <div className="p-4 rounded-xl bg-[#919191]/5 text-center text-[#919191]">
          Unable to determine your email address. Please ensure you are logged in.
        </div>
      )}

      {user?.email && (
        <>


          <DataTable
            columns={columns}
            data={assessments}
            rowKey="assessmentId"
            loading={loading}
            error={error}
            onRetry={fetchAssessments}
            searchable
            searchPlaceholder="Search assessments..."
            totalLabel="assessments"
            emptyMessage="No assessments found"
            rightAlignSearch={true}
            headerActions={
              <div>
                <h1 className="text-2xl font-bold text-[#53545a]">Assessments List</h1>
                <p className="text-sm mt-1 text-[#919191]">Review the assessments assigned to you and click an assessment to start your review.</p>
              </div>
            }
            belowSearchbarActions={
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className=" p-4 cardStyle">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0077b5] flex items-center justify-center flex-shrink-0">
                      <FiCheckCircle className="text-[#ffffff] text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#919191] mb-0.5">Review Completed</p>
                      <h3 className="text-xl font-bold text-[#53545a]">{completed}</h3>
                    </div>
                  </div>
                </div>
                <div className=" p-4 cardStyle">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#F47F2E] flex items-center justify-center flex-shrink-0">
                      <FiClock className="text-[#ffffff] text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#919191] mb-0.5">Processing</p>
                      <h3 className="text-xl font-bold text-[#53545a]">{processing}</h3>
                    </div>
                  </div>
                </div>
                <div className=" p-4 cardStyle">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0077b5] flex items-center justify-center flex-shrink-0">
                      <FiClock className="text-[#ffffff] text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#919191] mb-0.5">Pending Review</p>
                      <h3 className="text-xl font-bold text-[#53545a]">{pending}</h3>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default ReviewerAssessments;
